import { useCallback, useContext } from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { useAuthStore } from '@/shared/auth/store';
import {
  dwHidden,
  dwLabel,
  EMPTY_OVERLAY,
  normaliseOverlay,
  type WindowTranslationOverlay,
} from './windowTranslations';

/**
 * slice-4427 (ADR-0075): the session's window-translation overlay, fetched once per session
 * (`staleTime: Infinity`) from `GET /api/platform/window-translations`. The client and the
 * language come from the server session (JWT + user record / Change Language), never from here.
 *
 * Invalidated by the Window Translation editor's Save/Copy/Reset and by Change Language —
 * PB's `post f_phfc_change_language` re-translates every open window (`w_translate_maint.srw:334`,
 * `f_phfc_change_language.srf:25-38`). Keyed by the session id, so a new sign-in never sees the
 * previous user's labels.
 *
 * `enabled: false` skips the fetch (and returns the empty overlay) — browse screens without a
 * `dataObject` never ask.
 */
export const WINDOW_TRANSLATIONS_QUERY_KEY = ['platform', 'window-translations'] as const;

export function useWindowTranslations(enabled = true): WindowTranslationOverlay {
  const sessionId = useAuthStore((s) => s.session?.sessionId ?? '');
  // A label component rendered outside a QueryClientProvider (isolated tab/component tests,
  // static previews) shows its defaults instead of throwing: the query runs against an inert
  // client with the fetch disabled.
  const ctxClient = useContext(QueryClientContext);
  const { data } = useQuery(
    {
      queryKey: [...WINDOW_TRANSLATIONS_QUERY_KEY, sessionId],
      queryFn: async () =>
        normaliseOverlay(await api<unknown>('/api/platform/window-translations')),
      enabled: enabled && sessionId !== '' && ctxClient !== undefined,
      staleTime: Infinity,
      retry: false,
    },
    ctxClient ?? inertClient(),
  );
  return data ?? EMPTY_OVERLAY;
}

let inert: QueryClient | null = null;
function inertClient(): QueryClient {
  if (!inert) inert = new QueryClient();
  return inert;
}

/** One translated caption: the saved translation for `dw`.`obj`, else `defaultText`. */
export function useDwLabel(dataObject: string, object: string, defaultText: string): string {
  const overlay = useWindowTranslations();
  return dwLabel(overlay, dataObject, object, defaultText);
}

/**
 * Several captions of one DataWindow: `label(obj, default)` and `hidden(obj)`. For pages that
 * render a handful of labels from the same DataWindow.
 */
export function useDwText(dataObject: string): {
  label: (object: string, defaultText: string) => string;
  hidden: (object: string) => boolean;
} {
  const overlay = useWindowTranslations();
  const label = useCallback(
    (object: string, defaultText: string) => dwLabel(overlay, dataObject, object, defaultText),
    [overlay, dataObject],
  );
  const hidden = useCallback(
    (object: string) => dwHidden(overlay, dataObject, object),
    [overlay, dataObject],
  );
  return { label, hidden };
}
